/**
 * Scheda di un caso Salesforce (prova, QA26-F9.3): dati del caso, posizioni contestate, storia degli stati e Red Flag che
 * potrebbero riguardarlo (proposta, nessun collegamento salvato). Solo admin, sola lettura, oscurata nei feedback.
 */
import { Button, Card, Col, Descriptions, Row, Switch, Table, Tag } from "antd";
import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import type { SfCasoDto } from "@qa/shared";
import { api } from "../../api/client";
import { fmtData, fmtDataOra, fmtDec, fmtNum } from "../../lib/format";
import { PageHead, Ro, Sev, Stato, StatoRf, Vuoto } from "../../components/ui";
import { coloreStato, fmtValuta } from "./SfCasiPage";

const CAMPI_STATO = new Set(["CREATED", "STATUS", "OWNER", "RECORDTYPE"]);

export default function SfCasoPage() {
  const { id = "" } = useParams();
  const { t } = useTranslation();
  const [soloStati, setSoloStati] = useState(true);
  const q = useQuery({ queryKey: ["sf", "caso", id], queryFn: () => api.get<SfCasoDto>(`/admin/sf/casi/${id}`) });
  const sn = (v: boolean | null) => (v === null ? "—" : v ? t("admin.sf.si") : t("admin.sf.no"));
  const txt = (v: string | null) => v ?? "—";
  return (
    <Stato q={q} righe={12}>
      {(c) => (
        <>
          <PageHead
            crumbs={[{ label: t("portale.nome"), to: "/" }, { label: t("menu.gruppoAdmin") }, { label: t("menu.sfCasi"), to: "/admin/sf" }, { label: c.numero }]}
            title={`${c.numero} · ${c.account ?? ""}`}
            sub={c.oggetto ?? undefined}
            extra={
              <>
                <Ro>{t("admin.sf.prova")}</Ro>
                {c.sfUrl && (
                  <Button size="small" href={c.sfUrl} target="_blank" rel="noopener noreferrer" style={{ marginLeft: 8 }}>
                    {t("admin.sf.apriSalesforce")}
                  </Button>
                )}
              </>
            }
          />
          <div data-sensitive>
            <Row gutter={[16, 16]}>
              <Col xs={24} xl={16}>
                <Card size="small" title={t("admin.sf.dati")}>
                  <Descriptions size="small" column={{ xs: 1, md: 2 }}>
                    <Descriptions.Item label={t("admin.sf.stato")}>
                      <Tag color={coloreStato(c.stato)}>{txt(c.stato)}</Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label={t("admin.sf.recordType")}>{txt(c.recordType)}</Descriptions.Item>
                    <Descriptions.Item label={t("admin.sf.account")}>{txt(c.account)}</Descriptions.Item>
                    <Descriptions.Item label={t("admin.sf.brand")}>{txt(c.brand)}</Descriptions.Item>
                    <Descriptions.Item label={t("admin.sf.codiceSap")}>
                      <span className="mono">{txt(c.codiceSapCliente)}</span>
                    </Descriptions.Item>
                    <Descriptions.Item label={t("admin.sf.businessUnit")}>{txt(c.businessUnit)}</Descriptions.Item>
                    <Descriptions.Item label={t("admin.sf.apertoIl")}>{fmtDataOra(c.apertoIl)}</Descriptions.Item>
                    <Descriptions.Item label={t("admin.sf.chiusoIl")}>{fmtDataOra(c.chiusoIl)}</Descriptions.Item>
                    <Descriptions.Item label={t("admin.sf.origine")}>{txt(c.origine)}</Descriptions.Item>
                    <Descriptions.Item label={t("admin.sf.priorita")}>{txt(c.priorita)}</Descriptions.Item>
                    <Descriptions.Item label={t("admin.sf.responsabilita")}>{txt(c.responsabilita)}</Descriptions.Item>
                    <Descriptions.Item label={t("admin.sf.approvazioneNc")}>{sn(c.approvazioneNc)}</Descriptions.Item>
                    <Descriptions.Item label={t("admin.sf.statoApprovazione")}>{txt(c.statoApprovazione)}</Descriptions.Item>
                    <Descriptions.Item label={t("admin.sf.statoApprovazioneClaim")}>{txt(c.statoApprovazioneClaim)}</Descriptions.Item>
                    <Descriptions.Item label={t("admin.sf.risoluzione")}>{txt(c.risoluzione)}</Descriptions.Item>
                    <Descriptions.Item label={t("admin.sf.ufficio")}>{txt(c.ufficioRisoluzione)}</Descriptions.Item>
                    <Descriptions.Item label={t("admin.sf.reparto")}>{txt(c.reparto)}</Descriptions.Item>
                    <Descriptions.Item label={t("admin.sf.prodottoNonConforme")}>{txt(c.prodottoNonConforme)}</Descriptions.Item>
                    <Descriptions.Item label={t("admin.sf.tipoClaim")}>{txt(c.tipoClaim)}</Descriptions.Item>
                    <Descriptions.Item label={t("admin.sf.notaCredito")}>{sn(c.richiestaNotaCredito)}</Descriptions.Item>
                    <Descriptions.Item label={t("admin.sf.reso")}>{sn(c.richiestaReso)}</Descriptions.Item>
                    <Descriptions.Item label={t("admin.sf.ritardo")}>{sn(c.ritardoProduzione)}</Descriptions.Item>
                    <Descriptions.Item label={t("admin.sf.importoClaim")}>
                      <span className="num" data-sensitive>
                        {fmtValuta(c.importoClaim, c.valuta)}
                      </span>
                    </Descriptions.Item>
                    <Descriptions.Item label={t("admin.sf.valoreMerce")}>
                      <span className="num" data-sensitive>
                        {fmtValuta(c.valoreMerce, c.valuta)}
                      </span>
                    </Descriptions.Item>
                    <Descriptions.Item label={t("admin.sf.deltaNote")}>
                      <span className="num" data-sensitive>
                        {fmtValuta(c.deltaNoteCredito, c.valutaCosti ?? c.valuta)}
                      </span>
                    </Descriptions.Item>
                  </Descriptions>
                  <Descriptions size="small" column={1} style={{ marginTop: 8 }}>
                    <Descriptions.Item label={t("admin.sf.descrizione")}>
                      <span style={{ whiteSpace: "pre-wrap" }}>{txt(c.descrizione)}</span>
                    </Descriptions.Item>
                    <Descriptions.Item label={t("admin.sf.causa")}>{txt(c.causa)}</Descriptions.Item>
                    <Descriptions.Item label={t("admin.sf.storiaClaim")}>{txt(c.storiaClaim)}</Descriptions.Item>
                    <Descriptions.Item label={t("admin.sf.azione")}>{txt(c.azione)}</Descriptions.Item>
                    <Descriptions.Item label={t("admin.sf.commentoRisoluzione")}>{txt(c.commentoRisoluzione)}</Descriptions.Item>
                  </Descriptions>
                </Card>
              </Col>
              <Col xs={24} xl={8}>
                <Card size="small" title={t("admin.sf.redFlag")}>
                  <p className="muted" style={{ marginTop: 0 }}>
                    {t("admin.sf.redFlagNota")}
                  </p>
                  {c.redFlag.length ? (
                    <Table
                      size="small"
                      rowKey="id"
                      showHeader={false}
                      pagination={false}
                      dataSource={c.redFlag}
                      columns={[
                        { key: "c", width: 90, render: (_, r) => <Link className="mono" style={{ whiteSpace: "nowrap" }} to={`/redflag/segnalazioni/${r.id}`}>{r.codice}</Link> },
                        { key: "cl", render: (_, r) => r.cliente },
                        { key: "s", width: 80, render: (_, r) => <Sev s={r.severita} /> },
                        { key: "st", width: 110, render: (_, r) => <StatoRf s={r.stato} /> },
                        { key: "m", width: 120, render: (_, r) => <Tag color={r.motivo === "numero" ? "green" : "default"}>{r.motivo === "numero" ? t("admin.sf.perNumero") : t("admin.sf.perNome")}</Tag> },
                      ]}
                    />
                  ) : (
                    <Vuoto titolo={t("admin.sf.nessunaRedFlag")} />
                  )}
                </Card>
              </Col>
            </Row>
            <Card size="small" title={`${t("admin.sf.posizioni")} · ${c.posizioniDettaglio.length}`} style={{ marginTop: 16 }}>
              {c.posizioniDettaglio.length ? (
                <Table
                  size="small"
                  rowKey="id"
                  pagination={false}
                  scroll={{ x: 1100 }}
                  dataSource={c.posizioniDettaglio}
                  columns={[
                    { title: t("admin.sf.ordine"), key: "o", width: 140, render: (_, p) => <span className="mono">{`${p.ordine ?? "—"}/${p.posizioneOrdine ?? "—"}`}</span> },
                    { title: t("admin.sf.consegna"), key: "d", width: 100, render: (_, p) => <span className="num">{fmtData(p.consegnaIl)}</span> },
                    { title: t("admin.sf.prodotto"), key: "p", render: (_, p) => [p.categoria, p.famiglia, p.prodotto].filter(Boolean).join(" · ") || "—" },
                    { title: t("admin.sf.materiale"), key: "m", width: 220, render: (_, p) => <span>{p.materiale ? <span className="mono">{p.materiale} </span> : null}{p.testo ?? ""}</span> },
                    { title: t("admin.sf.produzione"), key: "pr", width: 100, render: (_, p) => p.produzione ?? "—" },
                    {
                      title: t("admin.sf.qta"),
                      key: "q",
                      width: 190,
                      align: "right",
                      render: (_, p) => <span className="num">{`${fmtNum(p.qtaConsegnata)} / ${fmtNum(p.qtaIspezionata)} / ${fmtNum(p.qtaDifettosa)}${p.percDifettosa !== null ? ` (${fmtDec(p.percDifettosa, 1)}%)` : ""}`}</span>,
                    },
                    { title: t("admin.sf.mancanti"), key: "mn", width: 120, align: "right", render: (_, p) => <span className="num">{`${fmtNum(p.qtaMancante)} / ${fmtNum(p.qtaSostituita)}`}</span> },
                    {
                      title: t("admin.sf.prezzo"),
                      key: "pu",
                      width: 120,
                      align: "right",
                      render: (_, p) => (
                        <span className="num" data-sensitive>
                          {fmtValuta(p.prezzoUnitario, p.valuta)}
                          {p.prezzoErrato ? <Tag color="red" style={{ marginLeft: 4 }}>{t("admin.sf.prezzoErrato")}</Tag> : null}
                        </span>
                      ),
                    },
                    {
                      title: t("admin.sf.importoClaim"),
                      key: "ic",
                      width: 130,
                      align: "right",
                      render: (_, p) => (
                        <span className="num" data-sensitive>
                          {fmtValuta(p.importoClaim, p.valuta)}
                        </span>
                      ),
                    },
                  ]}
                />
              ) : (
                <Vuoto titolo={t("admin.sf.nessunaPosizione")} />
              )}
            </Card>
            <Card
              size="small"
              title={t("admin.sf.storia")}
              style={{ marginTop: 16 }}
              extra={
                <span className="muted">
                  <Switch size="small" checked={soloStati} onChange={setSoloStati} style={{ marginRight: 6 }} />
                  {t("admin.sf.soloStati")}
                </span>
              }
            >
              {c.storia.length ? (
                <Table
                  size="small"
                  rowKey="id"
                  pagination={false}
                  dataSource={soloStati ? c.storia.filter((h) => CAMPI_STATO.has(h.campo)) : c.storia}
                  columns={[
                    { title: t("admin.registro.quando"), key: "at", width: 160, render: (_, h) => <span className="num">{fmtDataOra(h.at)}</span> },
                    { title: t("admin.sf.campo"), key: "c", width: 130, render: (_, h) => <span className="mono">{h.campo}</span> },
                    { title: t("admin.sf.da"), key: "d", render: (_, h) => h.da ?? "—" },
                    { title: t("admin.sf.a"), key: "a", render: (_, h) => h.a ?? "—" },
                  ]}
                />
              ) : (
                <Vuoto titolo={t("admin.sf.nessunaStoria")} />
              )}
            </Card>
          </div>
        </>
      )}
    </Stato>
  );
}
